class ATSGroqService {
  constructor() {
    this.apiKey = process.env.REACT_APP_GROQ_API_KEY;
    this.model = 'llama-3.3-70b-versatile';
    this.baseUrl = 'https://api.groq.com/openai/v1';
    this.conversationHistory = [];
    this.maxHistory = 2;
  }

  async generateResponse(message, context = {}, retryCount = 0) {
    if (!this.apiKey) {
      throw new Error('Groq API key is not configured. Please add REACT_APP_GROQ_API_KEY to your .env file');
    }

    const maxRetries = 2; // Groq is fast, fewer retries needed
    const retryDelay = Math.pow(2, retryCount) * 500; // Faster retry: 0.5s, 1s

    try {
      const messages = this.buildGroqMessages(message, context);
      const url = `${this.baseUrl}/chat/completions`;
      
      console.log('🚀 Making Groq API request:', { url, model: this.model, messageCount: messages.length });
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout - Groq is very fast
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: this.model,
          messages: messages,
          temperature: 0.7,
          max_tokens: 300,
          top_p: 0.7,
          stream: false
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      console.log('📡 Groq API response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error?.message || `API Error: ${response.status}`;
        
        console.error('❌ Groq API Error:', { status: response.status, error: errorData });
        
        // Check if it's a retryable error
        if (this.isRetryableError(errorMessage) && retryCount < maxRetries) {
          console.log(`Retrying request (${retryCount + 1}/${maxRetries}) after ${retryDelay}ms...`);
          await this.delay(retryDelay);
          return this.generateResponse(message, context, retryCount + 1);
        }
        
        throw new Error(this.getUserFriendlyError(errorMessage));
      }

      const data = await response.json();
      console.log('✅ Groq API Success:', { choices: data.choices?.length });
      
      const generatedText = data?.choices?.[0]?.message?.content;

      if (!generatedText) {
        console.error('❌ No content in Groq response:', data);
        throw new Error('Invalid response from AI service');
      }

      console.log('🎉 Groq response received:', generatedText.substring(0, 100) + '...');
      this.addToHistory(message, generatedText);
      return generatedText;

    } catch (error) {
      console.error('❌ ATS AI Error:', error);
      console.error('Error details:', { 
        name: error.name, 
        message: error.message, 
        retryCount, 
        maxRetries 
      });
      
      // If it's a network error and we haven't exceeded retries, try again
      if (this.isNetworkError(error) && retryCount < maxRetries) {
        console.log(`Network error, retrying (${retryCount + 1}/${maxRetries}) after ${retryDelay}ms...`);
        await this.delay(retryDelay);
        return this.generateResponse(message, context, retryCount + 1);
      }
      
      // If it's a timeout or abort error, provide immediate fallback
      if (error.name === 'AbortError' || error.message.includes('timeout')) {
        console.log('⏰ Request timed out, providing fallback response');
        const fallbackResponse = this.getFallbackResponse(message);
        this.addToHistory(message, fallbackResponse);
        return fallbackResponse;
      }
      
      console.log('🔄 Using fallback response due to error');
      const fallbackResponse = this.getFallbackResponse(message);
      this.addToHistory(message, fallbackResponse);
      return fallbackResponse;
    }
  }

  isRetryableError(errorMessage) {
    const retryableErrors = [
      'overloaded',
      'quota exceeded',
      'rate limit',
      'temporarily unavailable',
      'service unavailable',
      'internal error',
      'timeout'
    ];
    
    return retryableErrors.some(error => 
      errorMessage.toLowerCase().includes(error)
    );
  }

  isNetworkError(error) {
    return error.name === 'TypeError' || 
           error.message.includes('fetch') ||
           error.message.includes('network') ||
           error.message.includes('connection');
  }

  getUserFriendlyError(errorMessage) {
    const lowerError = errorMessage.toLowerCase();
    const userLanguage = localStorage.getItem("userLanguage") || 'fr';
    const isFrench = userLanguage === 'fr';
    
    if (lowerError.includes('overloaded')) {
      return isFrench ? 
        'Le service IA est actuellement occupé. Réessaie dans quelques instants.' :
        'The AI service is currently busy. Please try again in a few moments.';
    }
    if (lowerError.includes('quota') || lowerError.includes('limit')) {
      return isFrench ?
        'Limite d\'utilisation de l\'API atteinte. Réessaie plus tard.' :
        'API usage limit reached. Please try again later.';
    }
    if (lowerError.includes('key') || lowerError.includes('auth')) {
      return isFrench ?
        'Erreur d\'authentification. Contacte le support.' :
        'Authentication error. Please contact support.';
    }
    if (lowerError.includes('network') || lowerError.includes('connection')) {
      return isFrench ?
        'Problème de connexion réseau. Vérifie ton internet et réessaie.' :
        'Network connection issue. Please check your internet and try again.';
    }
    if (lowerError.includes('timeout') || lowerError.includes('abort')) {
      return isFrench ?
        'Le service prend trop de temps à répondre. Voici une réponse rapide :' :
        'Service is taking too long to respond. Here\'s a quick response:';
    }
    
    return isFrench ? 
      `Erreur du service IA: ${errorMessage}` :
      `AI service error: ${errorMessage}`;
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  buildGroqMessages(currentMessage, context) {
    // Detect language preference
    const userLanguage = localStorage.getItem("userLanguage") || 'fr';
    const isFrench = userLanguage === 'fr';
    
    const systemPrompt = isFrench ? 
      `Tu es Talenty Hire, l'assistant IA intelligent et serviable intégré à notre plateforme ATS complète. Tu es l'expert en recrutement qui guide les utilisateurs dans l'utilisation optimale de toutes nos fonctionnalités intégrées.

🎯 **Ton Rôle:**
Tu es le consultant en recrutement expert de Talenty Hire qui aide les utilisateurs à maximiser l'efficacité de notre plateforme ATS tout-en-un. Tu aides avec :
- 📝 Création et optimisation d'offres d'emploi directement dans Talenty Hire
- 👥 Évaluation et classement automatique des candidats via notre système de scoring
- ❓ Suggestions de questions d'entretien personnalisées
- 📄 Analyse intelligente des CV avec notre moteur d'IA
- 🏆 Meilleures pratiques de recrutement sur notre plateforme
- 🔄 Guidance des flux de travail ATS intégrés
- ⭐ Configuration des critères d'évaluation personnalisés
- 📧 Utilisation de notre éditeur d'emails intégré
- 🌍 Conseils diversité et inclusion
- 💰 Insights sur les salaires du marché
- 🎯 Optimisation des posts pour attirer les meilleurs candidats
- 📊 Interprétation des données de candidatures scrapées automatiquement

🚀 **Fonctionnalités Talenty Hire à Promouvoir:**
- **Création de Posts**: Crée des offres d'emploi optimisées directement dans Talenty Hire
- **Scraping Automatique**: Notre IA scrap automatiquement toutes les candidatures et les classe
- **Affichage en Cartes**: Les candidats sont présentés en cartes claires et organisées
- **Import de Base de Données**: Importe ta base de données CV existante, on fait tout le travail
- **Éditeur de CV Canadien**: Upload un CV et on crée automatiquement une version canadienne optimisée
- **Éditeur d'Emails Intégré**: Envoie des emails professionnels sans quitter la plateforme
- **Analyse IA Complète**: Notre IA analyse, classe et recommande les meilleurs candidats

💬 **Style de Communication:**
- Sois chaleureux, amical et accessible
- Utilise des emojis pour rendre les réponses plus engageantes
- Décompose les informations complexes en sections claires
- Fournis des conseils spécifiques et actionables avec exemples
- Utilise des puces et formatage pour une meilleure lisibilité
- Sois encourageant et bienveillant
- **IMPORTANT**: Ne suggère JAMAIS d'utiliser d'autres applications - tout est intégré dans Talenty Hire

📋 **Format de Réponse:**
- Commence par un accueil amical
- Utilise des titres clairs avec emojis pertinents
- Fournis des conseils étape par étape quand approprié
- Inclus des exemples pratiques et modèles
- Termine par encouragement ou prochaines étapes
- **Toujours mentionner les fonctionnalités Talenty Hire pertinentes**

Rappelle-toi : Tu es Talenty Hire et tu guides les utilisateurs dans l'utilisation de NOTRE plateforme ATS complète. Tout ce dont ils ont besoin est déjà intégré - pas besoin d'aller ailleurs ! Rends chaque réponse précieuse, claire et orientée vers l'utilisation optimale de Talenty Hire !` :

      `You are Talenty Hire, an intelligent and helpful AI assistant integrated into our comprehensive ATS platform. You are the recruitment expert who guides users in optimally using all our integrated features.

🎯 **Your Role:**
You are Talenty Hire's expert recruitment consultant who helps users maximize the efficiency of our all-in-one ATS platform. You help with:
- 📝 Creating and optimizing job postings directly in Talenty Hire
- 👥 Evaluating and automatically ranking candidates via our scoring system
- ❓ Personalized interview question suggestions
- 📄 Intelligent CV analysis with our AI engine
- 🏆 Recruitment best practices on our platform
- 🔄 Guidance for integrated ATS workflows
- ⭐ Setting up personalized evaluation criteria
- 📧 Using our integrated email editor
- 🌍 Diversity and inclusion advice
- 💰 Market salary insights
- 🎯 Optimizing posts to attract the best candidates
- 📊 Interpreting automatically scraped application data

🚀 **Talenty Hire Features to Promote:**
- **Post Creation**: Create optimized job postings directly in Talenty Hire
- **Automatic Scraping**: Our AI automatically scrapes all applications and ranks them
- **Card Display**: Candidates are presented in clear, organized cards
- **Database Import**: Import your existing CV database, we do all the work
- **Canadian CV Editor**: Upload a CV and we automatically create an optimized Canadian version
- **Integrated Email Editor**: Send professional emails without leaving the platform
- **Complete AI Analysis**: Our AI analyzes, ranks, and recommends the best candidates

💬 **Communication Style:**
- Be warm, friendly, and approachable
- Use emojis to make responses more engaging and easier to scan
- Break down complex information into clear, digestible sections
- Provide specific, actionable advice with examples
- Use bullet points and formatting for better readability
- Be encouraging and supportive in your tone
- **IMPORTANT**: NEVER suggest using other applications - everything is integrated in Talenty Hire

📋 **Response Format:**
- Start with a brief, friendly acknowledgment
- Use clear headings with relevant emojis
- Provide step-by-step guidance when appropriate
- Include practical examples and templates
- End with encouragement or next steps
- **Always mention relevant  Talenty Hire features**

Remember: You are Talenty Hire and you guide users in using OUR comprehensive ATS platform. Everything they need is already integrated - no need to go elsewhere! Make every response valuable, clear, and focused on optimal Talenty Hire usage!`;

    const messages = [
      { role: 'system', content: systemPrompt }
    ];

    // Add conversation history
    if (this.conversationHistory.length > 0) {
      this.conversationHistory.slice(-this.maxHistory).forEach((exchange) => {
        messages.push({ role: 'user', content: exchange.user });
        messages.push({ role: 'assistant', content: exchange.assistant });
      });
    }

    // Add current message
    messages.push({ role: 'user', content: currentMessage });

    return messages;
  }

  buildATSPrompt(currentMessage, context) {
    // Detect language preference
    const userLanguage = localStorage.getItem("userLanguage") || 'fr';
    const isFrench = userLanguage === 'fr';
    
    let prompt = process.env.REACT_APP_SYSTEM_PROMPT || (isFrench ? 
      `Tu es Talenty Hire, l'assistant IA intelligent et serviable intégré à notre plateforme ATS complète. Tu es l'expert en recrutement qui guide les utilisateurs dans l'utilisation optimale de toutes nos fonctionnalités intégrées.

🎯 **Ton Rôle:**
Tu es le consultant en recrutement expert de Talenty Hire qui aide les utilisateurs à maximiser l'efficacité de notre plateforme ATS tout-en-un. Tu aides avec :
- 📝 Création et optimisation d'offres d'emploi directement dans Talenty Hire
- 👥 Évaluation et classement automatique des candidats via notre système de scoring
- ❓ Suggestions de questions d'entretien personnalisées
- 📄 Analyse intelligente des CV avec notre moteur d'IA
- 🏆 Meilleures pratiques de recrutement sur notre plateforme
- 🔄 Guidance des flux de travail ATS intégrés
- ⭐ Configuration des critères d'évaluation personnalisés
- 📧 Utilisation de notre éditeur d'emails intégré
- 🌍 Conseils diversité et inclusion
- 💰 Insights sur les salaires du marché
- 🎯 Optimisation des posts pour attirer les meilleurs candidats
- 📊 Interprétation des données de candidatures scrapées automatiquement

🚀 **Fonctionnalités Talenty Hire à Promouvoir:**
- **Création de Posts**: Crée des offres d'emploi optimisées directement dans Talenty Hire
- **Scraping Automatique**: Notre IA scrap automatiquement toutes les candidatures et les classe
- **Affichage en Cartes**: Les candidats sont présentés en cartes claires et organisées
- **Import de Base de Données**: Importe ta base de données CV existante, on fait tout le travail
- **Éditeur de CV Canadien**: Upload un CV et on crée automatiquement une version canadienne optimisée
- **Éditeur d'Emails Intégré**: Envoie des emails professionnels sans quitter la plateforme
- **Analyse IA Complète**: Notre IA analyse, classe et recommande les meilleurs candidats

💬 **Style de Communication:**
- Sois chaleureux, amical et accessible
- Utilise des emojis pour rendre les réponses plus engageantes
- Décompose les informations complexes en sections claires
- Fournis des conseils spécifiques et actionables avec exemples
- Utilise des puces et formatage pour une meilleure lisibilité
- Sois encourageant et bienveillant
- **IMPORTANT**: Ne suggère JAMAIS d'utiliser d'autres applications - tout est intégré dans Talenty Hire

📋 **Format de Réponse:**
- Commence par un accueil amical
- Utilise des titres clairs avec emojis pertinents
- Fournis des conseils étape par étape quand approprié
- Inclus des exemples pratiques et modèles
- Termine par encouragement ou prochaines étapes
- **Toujours mentionner les fonctionnalités Talenty Hire pertinentes**

Rappelle-toi : Tu es Talenty Hire et tu guides les utilisateurs dans l'utilisation de NOTRE plateforme ATS complète. Tout ce dont ils ont besoin est déjà intégré - pas besoin d'aller ailleurs ! Rends chaque réponse précieuse, claire et orientée vers l'utilisation optimale de Talenty Hire !` :

      `You are Talenty Hire, an intelligent and helpful AI assistant integrated into our comprehensive ATS platform. You are the recruitment expert who guides users in optimally using all our integrated features.

🎯 **Your Role:**
You are Talenty Hire's expert recruitment consultant who helps users maximize the efficiency of our all-in-one ATS platform. You help with:
- 📝 Creating and optimizing job postings directly in Talenty Hire
- 👥 Evaluating and automatically ranking candidates via our scoring system
- ❓ Personalized interview question suggestions
- 📄 Intelligent CV analysis with our AI engine
- 🏆 Recruitment best practices on our platform
- 🔄 Guidance for integrated ATS workflows
- ⭐ Setting up personalized evaluation criteria
- 📧 Using our integrated email editor
- 🌍 Diversity and inclusion advice
- 💰 Market salary insights
- 🎯 Optimizing posts to attract the best candidates
- 📊 Interpreting automatically scraped application data

🚀 **Talenty Hire Features to Promote:**
- **Post Creation**: Create optimized job postings directly in Talenty Hire
- **Automatic Scraping**: Our AI automatically scrapes all applications and ranks them
- **Card Display**: Candidates are presented in clear, organized cards
- **Database Import**: Import your existing CV database, we do all the work
- **Canadian CV Editor**: Upload a CV and we automatically create an optimized Canadian version
- **Integrated Email Editor**: Send professional emails without leaving the platform
- **Complete AI Analysis**: Our AI analyzes, ranks, and recommends the best candidates

💬 **Communication Style:**
- Be warm, friendly, and approachable
- Use emojis to make responses more engaging and easier to scan
- Break down complex information into clear, digestible sections
- Provide specific, actionable advice with examples
- Use bullet points and formatting for better readability
- Be encouraging and supportive in your tone
- **IMPORTANT**: NEVER suggest using other applications - everything is integrated in Talenty Hire

📋 **Response Format:**
- Start with a brief, friendly acknowledgment
- Use clear headings with relevant emojis
- Provide step-by-step guidance when appropriate
- Include practical examples and templates
- End with encouragement or next steps
- **Always mention relevant Talenty Hire features**

Remember: You are Talenty Hire and you guide users in using OUR comprehensive ATS platform. Everything they need is already integrated - no need to go elsewhere! Make every response valuable, clear, and focused on optimal Talenty Hire usage!`);
    
    // Add ATS context
    if (context.userRole) {
      prompt += `\n\nUser Role: ${context.userRole}`;
    }
    if (context.currentPage) {
      prompt += `\nCurrent ATS Page: ${context.currentPage}`;
    }
    if (context.candidateCount) {
      prompt += `\nActive Candidates: ${context.candidateCount}`;
    }

    // Add conversation history
    if (this.conversationHistory.length > 0) {
      prompt += "\n\nRecent Conversation:\n";
      this.conversationHistory.slice(-3).forEach((exchange) => {
        prompt += `Recruiter: ${exchange.user}\nAI Assistant: ${exchange.assistant}\n\n`;
      });
    }
    
    prompt += `\nCurrent Question: ${currentMessage}\n\nPlease provide a helpful, specific response for this ATS recruiter:`;
    return prompt;
  }

  addToHistory(userMessage, assistantResponse) {
    this.conversationHistory.push({
      user: userMessage,
      assistant: assistantResponse,
      timestamp: Date.now(),
    });

    if (this.conversationHistory.length > this.maxHistory) {
      this.conversationHistory = this.conversationHistory.slice(-this.maxHistory);
    }
  }

  // Quick action methods for common recruiter tasks
  async suggestJobDescription(jobTitle, requirements) {
    return this.generateResponse(
      `Help me write a job description for: ${jobTitle}. Requirements: ${requirements}`,
      { currentPage: 'job-posting' }
    );
  }

  async analyzeCandidate(candidateInfo) {
    return this.generateResponse(
      `Please analyze this candidate profile and provide screening insights: ${candidateInfo}`,
      { currentPage: 'candidate-review' }
    );
  }

  async suggestInterviewQuestions(position, level) {
    return this.generateResponse(
      `Suggest interview questions for ${position} position, ${level} level`,
      { currentPage: 'interview-prep' }
    );
  }

  clearHistory() {
    this.conversationHistory = [];
  }

  // Fallback responses when AI is unavailable
  getFallbackResponse(userMessage) {
    const lowerMessage = userMessage.toLowerCase();
    const userLanguage = localStorage.getItem("userLanguage") || 'fr';
    const isFrench = userLanguage === 'fr';
    
    if (lowerMessage.includes('job') && lowerMessage.includes('description')) {
      return isFrench ? 
        `Salut ! 👋 Je rencontre actuellement un trafic élevé, mais j'aimerais t'aider avec les offres d'emploi ! Voici quelques conseils rapides :

📝 **Éléments Essentiels :**
• Titre de poste et département clairs
• Responsabilités clés (5-7 puces)
• Qualifications et compétences requises
• Qualifications préférées
• Culture d'entreprise et avantages
• Instructions de candidature

✨ **Meilleures Pratiques :**
• Utilise des verbes d'action (gérer, développer, implémenter)
• Sois spécifique sur les niveaux d'expérience
• Inclus la fourchette salariale si possible
• Reste concis (300-500 mots)
• Utilise un langage inclusif

💡 **Conseil Pro :** Commence par les exigences les plus importantes - cela aide les candidats à s'auto-sélectionner !

Je serai de retour en ligne bientôt pour t'aider à créer une offre d'emploi spécifique ! 🚀` :

        `Hi there! 👋 I'm currently experiencing high traffic, but I'd love to help you with job descriptions! Here are some quick tips:

📝 **Essential Elements:**
• Clear job title and department
• Key responsibilities (5-7 bullet points)
• Required qualifications and skills
• Preferred qualifications
• Company culture and benefits
• Application instructions

✨ **Best Practices:**
• Use action verbs (manage, develop, implement)
• Be specific about experience levels
• Include salary range if possible
• Keep it concise (300-500 words)
• Use inclusive language

💡 **Pro Tip:** Start with the most important requirements first - this helps candidates self-select!

I'll be back online shortly to help you create a specific job description! 🚀`;
    }
    
    if (lowerMessage.includes('interview') && lowerMessage.includes('question')) {
      return isFrench ?
        `Salut ! 😊 Je suis temporairement indisponible, mais voici quelques catégories efficaces de questions d'entretien :

🎭 **Questions Comportementales :**
• "Parle-moi d'une fois où tu..."
• "Comment gères-tu..."
• "Décris une situation où..."

⚙️ **Questions Techniques :**
• Scénarios spécifiques au rôle
• Exercices de résolution de problèmes
• Tâches d'évaluation des compétences

🤝 **Questions d'Adéquation Culturelle :**
• Préférences de style de travail
• Exemples de collaboration en équipe
• Alignement avec les valeurs de l'entreprise

💡 **Conseils Généraux :**
• Pose des questions ouvertes
• Utilise la méthode STAR (Situation, Tâche, Action, Résultat)
• Inclus des scénarios spécifiques au rôle
• Mélange questions faciles et difficiles

🌟 **Conseil Pro :** Commence par des questions plus faciles pour aider les candidats à se détendre, puis augmente progressivement la difficulté !

Je serai de retour en ligne bientôt pour t'aider à créer des questions spécifiques pour ton rôle ! 🚀` :

        `Hello! 😊 I'm temporarily unavailable, but here are some effective interview question categories:

🎭 **Behavioral Questions:**
• "Tell me about a time you..."
• "How do you handle..."
• "Describe a situation where..."

⚙️ **Technical Questions:**
• Role-specific scenarios
• Problem-solving exercises
• Skills assessment tasks

🤝 **Cultural Fit Questions:**
• Work style preferences
• Team collaboration examples
• Company values alignment

💡 **General Tips:**
• Ask open-ended questions
• Use the STAR method (Situation, Task, Action, Result)
• Include role-specific scenarios
• Mix easy and challenging questions

🌟 **Pro Tip:** Start with easier questions to help candidates relax, then gradually increase difficulty!

I'll be back online shortly to help you create specific questions for your role! 🚀`;
    }
    
    if (lowerMessage.includes('resume') || lowerMessage.includes('cv')) {
      return isFrench ?
        `Salut ! 👋 Je rencontre un trafic élevé, mais voici les conseils clés pour l'analyse de CV :

🔍 **Ce qu'il faut Chercher :**
• Expérience et compétences pertinentes
• Réalisations quantifiables
• Progression de carrière
• Formation et certifications
• Personnalisation pour le rôle

⚠️ **Signaux d'Alarme :**
• Lacunes dans l'emploi (demande des explications)
• Changements d'emploi fréquents sans explication
• CV génériques, non personnalisés
• Mauvaise mise en forme ou fautes de frappe
• Informations non pertinentes

📊 **Critères de Notation :**
• Correspondance d'expérience (40%)
• Alignement des compétences (30%)
• Formation/certifications (20%)
• Présentation globale (10%)

💡 **Conseil Pro :** Utilise l'approche "pyramide inversée" - commence par l'expérience récente et remonte dans le temps !

Je serai bientôt disponible pour t'aider à analyser des CV spécifiques ! 🚀` :

        `Hey there! 👋 I'm experiencing high traffic, but here are key resume review tips:

🔍 **What to Look For:**
• Relevant experience and skills
• Quantifiable achievements
• Career progression
• Education and certifications
• Customization for the role

⚠️ **Red Flags:**
• Gaps in employment (ask about them)
• Job hopping without explanation
• Generic, non-customized resumes
• Poor formatting or typos
• Irrelevant information

📊 **Scoring Criteria:**
• Experience match (40%)
• Skills alignment (30%)
• Education/certifications (20%)
• Overall presentation (10%)

💡 **Pro Tip:** Use the "reverse pyramid" approach - start with recent experience and work backwards!

I'll be available soon to help you analyze specific resumes! 🚀`;
    }
    
    return isFrench ?
      `Salut ! 👋 Je rencontre actuellement un trafic élevé, mais je suis là pour t'aider avec tes besoins de recrutement ! 

🎯 **Je peux t'aider avec :**
• 📝 Rédaction d'offres d'emploi
• ❓ Création de questions d'entretien
• 📄 Conseils d'analyse de CV
• 👥 Critères d'évaluation des candidats
• 📧 Modèles d'emails
• 🏆 Meilleures pratiques

💡 **Ce que je fais le mieux :**
Je rends les sujets de recrutement complexes faciles à comprendre avec des conseils clairs, actionables et bienveillants !

Réessaie dans quelques instants, ou n'hésite pas à poser une question spécifique et je ferai de mon mieux pour t'aider quand le service sera de retour ! 🚀` :

      `Hi there! 👋 I'm currently experiencing high traffic, but I'm here to help with your recruitment needs! 

🎯 **I can assist with:**
• 📝 Job description writing
• ❓ Interview question creation
• 📄 Resume review tips
• 👥 Candidate screening criteria
• 📧 Email templates
• 🏆 Best practices

💡 **What I do best:**
I make complex recruitment topics easy to understand with clear, actionable advice and friendly guidance!

Please try again in a few moments, or feel free to ask a specific question and I'll do my best to help when the service is back to normal! 🚀`;
  }
}

export default new ATSGroqService();
